import React from 'react';
import Select, { Field } from '../../ReusableComponents/Select';
import {
  FrequencyType,
  AGENT_FREQ_TYPES,
  WEEK_DAYS,
  HOURS,
  MINUTES,
} from '../../../utils/scheduleFrequency';

interface ScheduleFrequencyOptionsProps {
  freqType: FrequencyType;
  onFreqTypeChange: (type: FrequencyType) => void;
  hour: string;
  onHourChange: (hour: string) => void;
  minute: string;
  onMinuteChange: (minute: string) => void;
  ampm: 'AM' | 'PM';
  onAmpmChange: (ampm: 'AM' | 'PM') => void;
  weekDay: string;
  onWeekDayChange: (weekDay: string) => void;
  monthDay: number;
  onMonthDayChange: (monthDay: number) => void;
  cronExpression: string;
  onCronExpressionChange: (cron: string) => void;
  /** True when freqType is Custom and the entered cron fails validation — drives error styling. */
  isCronInvalid: boolean;
}

// A choice is a chip; the selected one fills. Same language as the Models page filters.
const chipClass = (active: boolean) => `eai-chip ${active ? 'eai-chip-active' : ''}`;

/** Shared cadence editor for the agent schedule modals (Create / Edit). Renders the frequency pills and the field that each frequency reveals (Custom cron, Hourly minute, Weekly day, Monthly day, or the time-of-day picker). Fully controlled — the parent owns the state and validation. */
const ScheduleFrequencyOptions: React.FC<ScheduleFrequencyOptionsProps> = ({
  freqType,
  onFreqTypeChange,
  hour,
  onHourChange,
  minute,
  onMinuteChange,
  ampm,
  onAmpmChange,
  weekDay,
  onWeekDayChange,
  monthDay,
  onMonthDayChange,
  cronExpression,
  onCronExpressionChange,
  isCronInvalid,
}) => {
  const showCronError = isCronInvalid && cronExpression.trim().length > 0;

  return (
    <>
      {/* Frequency type pills */}
      <Field label="Frequency">
        <div className="flex flex-wrap gap-2">
          {AGENT_FREQ_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onFreqTypeChange(type)}
              className={chipClass(freqType === type)}
            >
              {type}
            </button>
          ))}
        </div>
      </Field>

      {/* Conditional: Custom cron input */}
      {freqType === 'Custom' && (
        <Field
          label="Cron expression"
          htmlFor="schedule-cron"
          error={showCronError ? 'Enter a valid cron expression.' : undefined}
        >
          <input
            id="schedule-cron"
            value={cronExpression}
            onChange={(e) => onCronExpressionChange(e.target.value)}
            className={`eai-input ${showCronError ? 'border-eai-danger-border-strong focus:border-eai-danger-border-strong focus:ring-eai-danger-focus' : ''}`}
            placeholder="0 9 * * 1-5"
          />
          <p className="font-inter text-eai-body-xs text-eai-ink-faint">
            Order: <code>minute hour day month weekday</code>. Use <code>*</code> for “every”.
            <br />
            Example: <code>0 9 * * 1-5</code> runs at 9:00 AM, Monday–Friday.
          </p>
        </Field>
      )}

      {/* Conditional: Hourly minute picker */}
      {freqType === 'Hourly' && (
        <Field label="Minute of each hour">
          <Select value={minute} onChange={(e) => onMinuteChange(e.target.value)} containerClassName="w-24">
            {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </Field>
      )}

      {/* Conditional: Weekly day picker */}
      {freqType === 'Weekly' && (
        <Field label="Day">
          <div className="flex flex-wrap gap-2">
            {WEEK_DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => onWeekDayChange(day)}
                className={`${chipClass(weekDay === day)} w-14 justify-center`}
              >
                {day}
              </button>
            ))}
          </div>
        </Field>
      )}

      {/* Conditional: Monthly day picker */}
      {freqType === 'Monthly' && (
        <Field label="Day of month" htmlFor="schedule-month-day">
          <input
            id="schedule-month-day"
            type="number"
            min={1}
            max={31}
            value={monthDay}
            onChange={(e) => onMonthDayChange(Math.min(31, Math.max(1, Number(e.target.value))))}
            className="eai-input w-24"
          />
        </Field>
      )}

      {/* Time picker — hidden for Hourly (minute-only) and Custom (raw cron) */}
      {freqType !== 'Hourly' && freqType !== 'Custom' && (
        <Field label="Time">
          {/* Wraps on a phone, where the two pickers and the AM/PM track overrun the gutter */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={hour} onChange={(e) => onHourChange(e.target.value)} containerClassName="w-20">
              {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </Select>
            <span className="font-inter text-eai-body-sm text-eai-ink-subtle">:</span>
            <Select value={minute} onChange={(e) => onMinuteChange(e.target.value)} containerClassName="w-20">
              {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
            <div className="eai-seg">
              {(['AM', 'PM'] as const).map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => onAmpmChange(period)}
                  className={`eai-seg-item ${ampm === period ? 'eai-seg-item-active' : ''}`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </Field>
      )}
    </>
  );
};

export default ScheduleFrequencyOptions;
